// components/dashboard/OrdersChart.tsx
"use client";

import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

interface OrdersChartProps {
  data: { date: string; sales: number; orders: number }[];
}

export function OrdersChart({ data }: OrdersChartProps) {
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && theme === "dark";

  const colors = {
    primary: "#0f7a43",       // سبز برند
    primaryLight: "#159b57",  // سبز روشن
    grid: isDark ? "#1e3a2a" : "#e2ece3",
    axis: isDark ? "#94a3b8" : "#6f8377",
    tooltipBg: isDark ? "#0f2018" : "#ffffff",
    tooltipBorder: isDark ? "#1e3a2a" : "#e2ece3",
    tooltipText: isDark ? "#e5e7eb" : "#122a1c",
  };

  const fontStyle = {
    fontFamily: "var(--font-yekan-bakh), system-ui, Tahoma, sans-serif",
    fontSize: 11,
  };

  return (
    <div className="materio-card h-full flex flex-col">
      <div className="p-5 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-foreground">تعداد سفارشات</h3>
            <p className="text-xs text-muted-foreground mt-1">
              سفارشات ثبت‌شده در ۷ روز اخیر
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-primary" />
            <span className="text-xs text-muted-foreground">سفارش</span>
          </div>
        </div>
      </div>
      <div className="p-5 pt-0 flex-1">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            barCategoryGap="40%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={colors.grid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={{ ...fontStyle, fill: colors.axis }}
              axisLine={false}
              tickLine={false}
              dy={10}
            />
            <YAxis
              tick={{ ...fontStyle, fill: colors.axis }}
              axisLine={false}
              tickLine={false}
              dx={-10}
            />
            <Tooltip
              cursor={{
                fill: isDark
                  ? "rgba(15,122,67,0.15)"
                  : "rgba(15,122,67,0.06)",
              }}
              formatter={(value: number) => [
                `${value.toLocaleString("fa-IR")} سفارش`,
                "تعداد",
              ]}
              contentStyle={{
                fontFamily:
                  "var(--font-yekan-bakh), system-ui, Tahoma, sans-serif",
                borderRadius: "12px",
                border: `1px solid ${colors.tooltipBorder}`,
                backgroundColor: colors.tooltipBg,
                color: colors.tooltipText,
                boxShadow: "0 4px 20px rgba(8,60,32,0.10)",
                direction: "rtl",
                fontSize: 13,
                padding: "8px 12px",
              }}
              labelStyle={{
                fontFamily:
                  "var(--font-yekan-bakh), system-ui, Tahoma, sans-serif",
                fontWeight: "bold",
                color: colors.primary,
                marginBottom: 4,
              }}
            />
            <Bar
              dataKey="orders"
              radius={[6, 6, 0, 0]}
              maxBarSize={32}
              animationDuration={1500}
              animationEasing="ease-out"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    index === data.length - 1
                      ? colors.primary
                      : colors.primaryLight
                  }
                  fillOpacity={1}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}